import { ArrivalInput, DepartInput, followDate, moveReturnToShuttle } from '../components/ArrivalInput';
import { useEffect, useState } from 'react';
import { api, type DisptInput } from '../lib/api';
import { ErrorBox, moneyInput, MoneyInput } from '../components/DataTable';
import { isVatIncl, VatPreview, VatToggle } from '../components/Vat';
import { CarKindSelect, CarYearSelect } from '../components/CarKindPicker';
import { BIDDING_TYPES, FinGrpPicker } from '../components/RecvFields';

/** 노선 수정 창 「입찰 공개」(10-27 way) — 공개·유형·마감일은 **접수** 칸이라 저장은 `setDisptBidding` 따로(`updateDispt` 는 노선 칸만). */
type Bid = { isBidding: boolean; biddingType: string; closeDate: string; off: boolean };

/**
 * 노선 등록 / 수정 — **기존 접수에 한 줄씩** 붙이거나 고칠 때.
 *
 * 신규 접수는 여기로 오지 않는다. 접수와 노선을 한 화면에서 같이 받는 `RecvIntakeForm` 이 담당한다.
 *
 * - **편도면 복편 시각 칸이 사라진다.** `is_shuttle` 은 통근이 아니라 왕복이고,
 *   편도일 때 서버가 복편 시각을 NULL 로 강제한다. 입력칸을 남겨두면 "넣었는데 저장 안 됨"이 된다.
 * - 금액은 문자열 그대로 보낸다. number 로 왕복시키면 소수 4자리가 깎인다
 *   (이관에서 0.56원이 어긋났던 그 자리와 같은 부류).
 * - 정산월이 마감돼 있으면 서버가 거부한다. 그 문장을 그대로 보여준다.
 */

export default function DisptForm({
  recvId,
  disptId,
  copyFromId,
  onClose,
  onSaved,
}: {
  recvId: number;
  disptId?: number;
  /** 노선 복사(#178) — 이 노선의 값을 채운 채 **새 노선**으로 등록한다(배정·경유지는 안 따라온다). */
  copyFromId?: number;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const editing = disptId !== undefined;
  // #344 「노선 추가」도 빈 칸이 아니라 **이 접수에 마지막으로 등록한 노선**으로 채워 연다 — 같은 접수의 노선은 대개
  // 날짜만 다르다(윤일향). 노선이 없는 접수면 빈 칸 그대로. 복사와 같은 채움이라 배정·경유지는 안 따라온다.
  const adding = !editing && copyFromId === undefined;
  const [prefillId, setPrefillId] = useState<number | undefined>(undefined);
  const sourceId = editing ? disptId : (copyFromId ?? prefillId);
  const [d, setD] = useState<DisptInput>({ recvId, carCnt: 1, isShuttle: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bid, setBid] = useState<Bid | null>(null);
  const [bid0, setBid0] = useState<Bid | null>(null);
  // 청구법인(11-03 #488) — 접수 칸이라 바뀐 때만 `setDisptFinGrp` 따로 저장(그 접수 노선 전부에 걸린다).
  const [finGrp, setFinGrp] = useState<number | undefined>(undefined);
  const [finGrp0, setFinGrp0] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!adding) return;
    let cancelled = false;
    api
      .listDispts({ recvId, limit: 1, sort: 'dispt_id', dir: 'desc' })
      .then((r) => {
        if (cancelled) return;
        const last = r.rows[0];
        if (last) setPrefillId(Number(last.dispt_id));
        else setLoading(false);
      })
      // 못 읽어도 막지 않는다 — 빈 칸으로 넣으면 된다.
      .catch(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [adding, recvId]);

  useEffect(() => {
    if (sourceId === undefined) return;
    let cancelled = false;
    api
      .getDispt({ disptId: sourceId })
      .then((r) => {
        if (cancelled) return;
        setD({
          // 복사면 번호를 비워 새 노선으로 — 접수는 그 노선의 접수 그대로.
          disptId: editing ? Number(r.dispt_id) : undefined,
          recvId: Number(r.recv_id),
          disptName: (r.dispt_name as string) ?? '',
          carKind: (r.car_kind as string) ?? '',
          carYear: r.car_year === null ? undefined : Number(r.car_year),
          carCnt: r.car_cnt === null ? 1 : Number(r.car_cnt),
          startLoc: (r.start_loc as string) ?? '',
          startTime: (r.start_time as string) ?? '',
          endLoc: (r.end_loc as string) ?? '',
          endTime: (r.end_time as string) ?? '',
          isShuttle: r.is_shuttle === true,
          endDeptTime: (r.end_dept_time as string) ?? '',
          startArrvTime: (r.start_arrv_time as string) ?? '',
          // 포함가로 저장된 노선은 포함가를 칸에 띄운다(#87) — 공급가를 띄우면 다시 저장할 때 ÷1.1 이 또 걸린다.
          ...(isVatIncl(r.cost_cust, r.cost_cust_add_tax)
            ? { costCust: moneyInput(r.cost_cust_add_tax), vatIncl: true }
            : { costCust: moneyInput(r.cost_cust), vatIncl: false }),
          costDriver: moneyInput(r.cost_driver),
          driveMon: (r.drive_mon as string) ?? '',
          isMeal: r.is_meal === true,
          isLodge: r.is_lodge === true,
          request: (r.request as string) ?? '',
        });
        if (editing) {
          const b: Bid = {
            isBidding: r.is_bidding === true,
            biddingType: (r.bidding_type as string) ?? '',
            closeDate: String(r.close_date ?? '').slice(0, 10),
            off: r.bid_off === true,
          };
          setBid(b);
          setBid0(b);
          const fg = r.fin_grp_id === null || r.fin_grp_id === undefined ? undefined : Number(r.fin_grp_id);
          setFinGrp(fg);
          setFinGrp0(fg);
        }
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [editing, sourceId]);

  const set = (patch: Partial<DisptInput>) => setD((cur) => ({ ...cur, ...patch }));

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        const bidChanged = !!bid && !!bid0 &&
          (bid.isBidding !== bid0.isBidding || bid.biddingType !== bid0.biddingType || bid.closeDate !== bid0.closeDate);
        if (bidChanged && bid.isBidding && !bid.closeDate) throw new Error('기사에게 공개하려면 입찰 마감일을 입력하세요.');
        await api.updateDispt(d);
        // 노선 칸을 먼저 저장하고 입찰 칸은 바뀐 때만 — 받는 인승·노선별 빼기는 안 보내 그대로 둔다.
        if (bidChanged && bid && disptId !== undefined) {
          await api.setDisptBidding({
            disptId,
            isBidding: bid.isBidding,
            biddingType: bid.isBidding ? bid.biddingType || undefined : undefined,
            closeDate: bid.closeDate || undefined,
            carYear: d.carYear ?? 0,
          });
        }
        if (finGrp !== undefined && finGrp !== finGrp0 && disptId !== undefined) {
          await api.setDisptFinGrp({ disptId, finGrpId: finGrp });
        }
        onSaved(`노선 ${disptId}번을 수정했습니다.`);
      } else {
        const r = await api.createDispt(d);
        onSaved(copyFromId ? `노선 ${copyFromId}번을 복사해 노선 ${r.disptId}번을 등록했습니다.` : `노선 ${r.disptId}번을 등록했습니다.`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <div className="card space-y-4 p-5">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-bold">
          {editing ? `노선 수정 (${disptId}번)` : copyFromId ? `노선 복사 · 노선 ${copyFromId}번 → 새 노선` : `노선 추가 · 접수 ${recvId}번`}
        </h2>
        {copyFromId && <span className="text-xs text-ink-faint">값을 고친 뒤 등록하면 같은 접수에 새 노선이 생깁니다 · 배차 차량은 따라오지 않습니다</span>}
        {adding && prefillId !== undefined && (
          <span className="text-xs text-ink-faint">노선 {prefillId}번 내용을 채워 두었습니다 · 날짜만 바꿔 등록하시면 됩니다</span>
        )}
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={saving}>
          취소
        </button>
        <button className="btn-primary" onClick={save} disabled={saving || loading}>
          {saving ? '저장 중…' : editing ? '수정' : '등록'}
        </button>
      </div>

      {error && <ErrorBox message={error} />}
      {loading && <div className="text-sm text-ink-soft">불러오는 중…</div>}

      {!loading && (
        <div className="grid gap-4 md:grid-cols-2">
          <F label="노선명">
            <input className="field" value={d.disptName ?? ''} onChange={(e) => set({ disptName: e.target.value })} />
          </F>
          <F label="차종" hint="차종 설정 목록에서 고릅니다">
            <CarKindSelect value={d.carKind} onChange={(v) => set({ carKind: v })} />
          </F>
          <F label="차량 연식 제한" hint="기사앱 입찰은 이 해 수 이내(연식 등록된) 차량만 신청됩니다">
            <CarYearSelect value={d.carYear} onChange={(v) => set({ carYear: v })} />
          </F>

          {editing && bid && (
            <div className="space-y-2 rounded border border-blue-200 bg-blue-50/50 p-3 md:col-span-2">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="font-medium">입찰 공개</span>
                {[
                  { v: false, t: '비공개' },
                  { v: true, t: '기사에게 공개' },
                ].map((o) => (
                  <label key={String(o.v)} className="flex cursor-pointer select-none items-center gap-1.5">
                    <input type="radio" name="dispt-form-bid" checked={bid.isBidding === o.v}
                      onChange={() => setBid({ ...bid, isBidding: o.v })} />
                    {o.t}
                  </label>
                ))}
                {bid.isBidding && (
                  <>
                    <select className="field w-auto py-1" value={bid.biddingType}
                      onChange={(e) => setBid({ ...bid, biddingType: e.target.value })}>
                      <option value="">입찰 유형 선택 안 함</option>
                      {BIDDING_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                      {bid.biddingType && !BIDDING_TYPES.includes(bid.biddingType) && (
                        <option value={bid.biddingType}>{bid.biddingType}</option>
                      )}
                    </select>
                    <span className="flex items-center gap-1.5">
                      <span className="text-xs text-ink-soft">입찰 마감일<span className="text-red-600">*</span></span>
                      <input type="date" className="field w-auto py-1" value={bid.closeDate}
                        onChange={(e) => setBid({ ...bid, closeDate: e.target.value })} />
                    </span>
                  </>
                )}
              </div>
              <div className="text-xs text-ink-faint">
                공개·입찰 유형·마감일은 이 접수의 노선 전체에 같이 적용됩니다 · 받는 인승과 노선별로 입찰에서 빼기는 노선 상세 「입찰 공개·연식」에서 합니다
                {bid.isBidding && bid.off && <span className="ml-1 text-amber-700">· 이 노선은 지금 입찰에서 빠져 있습니다(노선 상세에서 넣을 수 있습니다)</span>}
              </div>
            </div>
          )}

          <F label="출발지" required>
            <input className="field" value={d.startLoc ?? ''} onChange={(e) => set({ startLoc: e.target.value })} />
          </F>
          <F label="출발 일시" required>
            <DepartInput value={d.startTime} onChange={(v) => setD((cur) => ({
              ...cur, startTime: v, endTime: followDate(cur.startTime, v, cur.endTime) ?? cur.endTime,
              // 정산월이 비었거나 옛 출발월 그대로면 새 출발월을 따라간다(채워 둔 노선 「날짜만 바꿔 등록」 · issues 317).
              // 사람이 다른 달로 적어 둔 값은 그대로.
              driveMon: !cur.driveMon || cur.driveMon === (cur.startTime ?? '').slice(0, 7)
                ? (/^\d{4}-\d{2}/.test(v ?? '') ? (v as string).slice(0, 7) : cur.driveMon)
                : cur.driveMon,
            }))} />
          </F>

          <F label="도착지">
            <input className="field" value={d.endLoc ?? ''} onChange={(e) => set({ endLoc: e.target.value })} />
          </F>
          <F label="도착 일시" required>
            <ArrivalInput value={d.endTime} onChange={(v) => set({ endTime: v })} />
          </F>

          <F label="필요 대수" required>
            <input
              type="number"
              min={1}
              className="field"
              value={d.carCnt ?? 1}
              onChange={(e) => set({ carCnt: Number(e.target.value) })}
            />
          </F>
          <F label="정산월" hint="YYYY-MM · 출발일을 넣으면 그 달로 채워집니다(다른 달이면 고치세요) · 일반 노선은 비워 두면 출발월로 저장 · 마감된 달이면 거부됩니다">
            <input
              className="field"
              placeholder="2026-08"
              value={d.driveMon ?? ''}
              onChange={(e) => set({ driveMon: e.target.value })}
            />
          </F>

          {/* 청구액 = 노선 총액, 운행료 = 1대당 단가(CLAUDE.md 불변식 · issues 179). */}
          <F label="청구액" hint="노선 전체 총액입니다 — 대수를 곱하지 않습니다">
            <div className="space-y-1">
              <VatToggle value={d.vatIncl === true} onChange={(v) => set({ vatIncl: v })} />
              <MoneyInput value={d.costCust} onChange={(v) => set({ costCust: v })} />
              <VatPreview text={d.costCust} incl={d.vatIncl === true} />
            </div>
          </F>
          <F label="운행료" hint="1대당 단가입니다 — 합계 = 단가 × 대수">
            <MoneyInput value={d.costDriver} onChange={(v) => set({ costDriver: v })} />
          </F>
          {editing && (
            <>
              {/* 운행료 바로 아래(오른쪽 칸)에 두려고 왼쪽을 비운다 — #488 첨부가 가리킨 자리. */}
              <div className="hidden md:block" />
              <F label="청구법인" hint="접수 칸입니다 — 이 접수의 노선 전부에 같이 바뀝니다">
                <FinGrpPicker value={finGrp} onPick={(id) => { if (id !== undefined) setFinGrp(id); }} />
              </F>
            </>
          )}

          <div className="md:col-span-2">
            <label className="flex cursor-pointer select-none items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={d.isShuttle === true}
                onChange={(e) =>
                  set(e.target.checked ? (moveReturnToShuttle(d) ?? { isShuttle: true }) : { isShuttle: false })
                }
              />
              왕복 운행
            </label>
          </div>

          {d.isShuttle && (
            <>
              <F label="복편 출발 일시" hint="복귀 시각을 모르면 날짜만 넣으셔도 됩니다">
                <ArrivalInput value={d.endDeptTime} onChange={(v) => setD((cur) => ({
                  ...cur, endDeptTime: v, startArrvTime: followDate(cur.endDeptTime, v, cur.startArrvTime) ?? cur.startArrvTime,
                }))} />
              </F>
              <F label="복편 도착 일시">
                <ArrivalInput value={d.startArrvTime} onChange={(v) => set({ startArrvTime: v })} />
              </F>
            </>
          )}

          <div className="flex gap-5 md:col-span-2">
            <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
              <input type="checkbox" checked={d.isMeal === true} onChange={(e) => set({ isMeal: e.target.checked })} />
              식사 포함
            </label>
            <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={d.isLodge === true}
                onChange={(e) => set({ isLodge: e.target.checked, ...(e.target.checked ? moveReturnToShuttle(d) : null) })}
              />
              숙박 포함
            </label>
          </div>

          {/* #275 노선 참고사항 — 입찰 때 기사앱 입찰 카드에, 노선 상세에 관리자에게 보인다. */}
          <div className="md:col-span-2">
            <F label="참고사항" hint="입찰 때 기사님과 노선 조회 때 관리자가 봅니다 · 최대 500자">
              <textarea className="field min-h-[4.5rem]" maxLength={500} value={d.request ?? ''}
                        onChange={(e) => set({ request: e.target.value })} />
            </F>
          </div>
        </div>
      )}
    </div>
  );
}

function F({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">
        {label}
        {required && <span className="ml-0.5 text-red-600">*</span>}
        {hint && <span className="ml-2 text-xs font-normal text-ink-faint">{hint}</span>}
      </span>
      {children}
    </label>
  );
}
